import React, { useCallback } from "react";

import { Stack, Typography } from "@mui/material";
import {
  AdvancedSearchCharacteristicIcon,
  AdvancedSearchDealIcon,
  AdvancedSearchDocumentIcon,
  AdvancedSearchFileIcon,
  AdvancedSearchFormIcon,
  WorkspaceFolderCloseIcon
} from "assets/material-custom-icons";
import { useNavigate } from "hooks";
import { capitalize } from "lodash";
import { useDealActions } from "store";
import { ISearchItem, SearchItemType } from "store/types";

const iconStyle = {
  width: "20px",
  height: "20px"
};
interface IProps {
  item: ISearchItem;
  resetSearchState: () => void;
}

const icon = {
  [SearchItemType.Deal]: <AdvancedSearchDealIcon sx={iconStyle} />,
  [SearchItemType.File]: <AdvancedSearchFileIcon sx={iconStyle} />,
  [SearchItemType.Document]: <AdvancedSearchDocumentIcon sx={iconStyle} />,
  [SearchItemType.Characteristic]: <AdvancedSearchCharacteristicIcon sx={iconStyle} />,
  [SearchItemType.Form]: <AdvancedSearchFormIcon sx={iconStyle} />,
  [SearchItemType.Folder]: <WorkspaceFolderCloseIcon sx={iconStyle} />
};

export const ListItem: React.FC<IProps> = ({ item, resetSearchState }) => {
  const { setCharacteristicsManageAction } = useDealActions();
  const navigate = useNavigate();

  const onClickHandler = useCallback(() => {
    const { type, deal_id, id, section_id, deal_title, folder_kind, email_id } = item;

    if (type === SearchItemType.Deal) {
      navigate({ route: "dealWorkspace", params: { deal_id } });
    }

    if (type === SearchItemType.Folder && folder_kind && !email_id) {
      const folderRouteMap = {
        folder: {
          route: "dealFolder" as const,
          params: { deal_id, folder_id: id }
        },
        dataroom: {
          route: "dealDataRoom" as const,
          params: { deal_id, folder_id: id },
          search: { section: id }
        }
      };

      const navigationConfig = folderRouteMap[folder_kind as keyof typeof folderRouteMap];
      if (navigationConfig) {
        navigate(navigationConfig);
      }
    }

    if (type === SearchItemType.Characteristic && deal_id) {
      navigate({ route: "dealWorkspace", params: { deal_id } });
      setCharacteristicsManageAction({
        dealId: deal_id,
        dealName: deal_title,
        characteristicId: id
      });
    }

    if (type === SearchItemType.Document && section_id) {
      navigate({
        route: "dealDocument",
        params: { deal_id, document_id: id },
        search: { section: section_id }
      });
    }

    if (type === SearchItemType.Form && section_id) {
      navigate({
        route: "dealForm",
        params: { deal_id, form_id: id },
        search: { section: section_id }
      });
    }

    if (type === SearchItemType.File && email_id) {
      navigate({ route: "dealEmail", params: { deal_id, email_id } });
    }

    if (type === SearchItemType.File && section_id && !email_id) {
      navigate({
        route: folder_kind === "dataroom" ? "dealDataRoom" : "dealFolder",
        params: folder_kind === "dataroom" ? { deal_id, folder_id: section_id } : { deal_id, folder_id: section_id },
        search: { section: section_id }
      });
    }

    resetSearchState();
  }, [item.deal_title, item.type, resetSearchState]);

  return (
    <Stack
      onClick={onClickHandler}
      sx={{
        padding: "10px 20px",
        "&:hover": {
          backgroundColor: "#ECF1F9",
          cursor: "pointer"
        }
      }}
      direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      <Stack direction="row" gap="10px">
        <Stack direction="row" gap="10px" alignItems="center">
          {icon[item.type]}
        </Stack>
        <Stack direction="column">
          <Typography sx={{ fontSize: "16px", whiteSpace: "pre-wrap" }}>{item.result}</Typography>
          <Typography sx={{ fontSize: "14px", color: "#92A7BC" }}>{item.deal_title}</Typography>
        </Stack>
      </Stack>
      <Stack direction="row" gap="10px">
        <Typography sx={{ fontSize: "14px", color: "#92A7BC" }}>{capitalize(item.type)}</Typography>
      </Stack>
    </Stack>
  );
};
